import { Box, Skeleton, Typography } from "@mui/material";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { CertificateIcon } from "../../../assets/icons/MUI/EntitiesIcons";
import { colors } from "../../../theme/palette";
import { Certificate } from "../../../types";

type CertificateProps = {
  certificate: Certificate;
};

const CertificateOrLicense = ({
  certificate: { title, startDate, endDate },
}: CertificateProps) => {
  // Helper function to safely format dates
  const safeFormatDate = (dateStr?: string) => {
    try {
      return dateStr ? format(parseISO(dateStr), "MMM yyyy") : "";
    } catch (error) {
      // console.error(`Invalid date format: ${dateStr}`, error);
      return "";
    }
  };

  // Helper function to safely compute time ago
  const safeFormatDistance = (dateStr?: string) => {
    try {
      return dateStr
        ? formatDistanceToNow(parseISO(dateStr), { addSuffix: true })
        : "";
    } catch (error) {
      // console.error(`Invalid date format: ${dateStr}`, error);
      return "";
    }
  };

  const formattedStartDate = safeFormatDate(startDate);
  const formattedEndDate = safeFormatDate(endDate);
  const timeAgo = safeFormatDistance(endDate || startDate);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        paddingBottom: "20px",
        marginBottom: "20px",
        borderBottom: `1px solid ${colors.Gray100}`,
        flexDirection: {
          xs: "column",
          sm: "row",
        },
        rowGap: "20px",
        "&:last-child": {
          paddingBottom: 0,
          marginBottom: 0,
          borderBottom: "none",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          flex: 1,
        }}
      >
        <CertificateIcon style={{ width: 48, height: 48 }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="body14Bold"
            sx={{
              fontWeight: 700,
            }}
          >
            {title}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          columnGap: "10px",
        }}
      >
        <Typography
          variant="body10Bold"
          sx={{
            color: colors.Dark600,
            fontWeight: 600,
          }}
        >
          {formattedStartDate +
            (formattedEndDate ? " - " + formattedEndDate : "")}
        </Typography>
        <Typography
          variant="body10Bold"
          sx={{
            color: colors.Gray400,
            fontWeight: 600,
            fontSize: "10px",
            minWidth: "100px",
            textAlign: "right",
          }}
        >
          {timeAgo}
        </Typography>
      </Box>
    </Box>
  );
};

export default CertificateOrLicense;

export const CertificateOrLicenseLoading = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      paddingBottom: "20px",
      marginBottom: "20px",
      borderBottom: `1px solid ${colors.Gray100}`,
      flexDirection: {
        xs: "column",
        sm: "row",
      },
      rowGap: "20px",
      "&:last-child": {
        paddingBottom: 0,
        marginBottom: 0,
        borderBottom: "none",
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        gap: "16px",
        flex: 1,
      }}
    >
      <Skeleton variant="circular" width={48} height={48} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Skeleton variant="text" width="200px" height={24} />
      </Box>
    </Box>
    <Box
      sx={{
        display: "flex",
        columnGap: "10px",
      }}
    >
      <Skeleton variant="text" width={80} height={16} />
      <Skeleton variant="text" width={60} height={16} />
    </Box>
  </Box>
);
